import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useTilt, Glare } from "@/components/marketing/Depth4D";

const MEDIA = `${process.env.REACT_APP_BACKEND_URL}/api/media/hero`;
const V = "?v=11";
const HD = `${MEDIA}/ari-hero-1080.mp4${V}`;
const UHD = `${MEDIA}/ari-hero-2160.mp4${V}`;
const WEBM = `${MEDIA}/ari-hero-1080.webm${V}`;
const POSTER = `${MEDIA}/ari-hero-poster.jpg${V}`;
const RADIUS = "rounded-[20px] sm:rounded-[28px]";
const TOUCH_Q = "(pointer: coarse), (max-width: 1023px)";
// 4K only where the screen can show it (large / high-density desktop displays).
const wantsUhd = () => !window.matchMedia(TOUCH_Q).matches && window.screen.width * (window.devicePixelRatio || 1) >= 2560;

const useMedia = (q) => {
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [q]);
  return m;
};

const SoundToggle = ({ muted, onClick }) => {
  const { t } = useLang();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t(muted ? "Turn sound on" : "Turn sound off")}
      aria-pressed={!muted}
      data-testid="hero-video-sound-toggle"
      className="absolute bottom-3 right-3 sm:bottom-5 sm:right-5 z-[4] h-10 w-10 sm:h-11 sm:w-11 rounded-full flex items-center justify-center text-[#D4AF37] bg-[#0A0A0B]/70 border border-[#D4AF37]/45 backdrop-blur-md shadow-[0_4px_18px_rgba(0,0,0,0.45)] transition-[background-color,border-color,transform] duration-300 hover:bg-[#0A0A0B]/90 hover:border-[#D4AF37]/80 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/70"
    >
      {muted ? <VolumeX className="h-4 w-4" strokeWidth={1.6} /> : <Volume2 className="h-4 w-4" strokeWidth={1.6} />}
    </button>
  );
};

export const HeroVideo = () => {
  const reduce = useReducedMotion();
  const touch = useMedia(TOUCH_Q);
  const vref = useRef(null);
  const [muted, setMuted] = useState(true);
  const tilt = useTilt(2);
  const [src] = useState(() => (wantsUhd() ? UHD : HD));

  // Safari/iOS need the real `muted` attribute for autoplay; if autoplay is blocked, the first tap anywhere starts it.
  useEffect(() => {
    const v = vref.current;
    if (!v || reduce) return undefined;
    v.muted = true; v.defaultMuted = true; v.loop = true;
    v.setAttribute("muted", ""); v.setAttribute("playsinline", ""); v.setAttribute("webkit-playsinline", "");
    const kick = () => { if (v.paused) v.play()?.catch?.(() => {}); };
    const onEnded = () => { v.currentTime = 0; kick(); };
    v.addEventListener("ended", onEnded);
    const evts = ["touchstart", "pointerdown", "click", "keydown"];
    evts.forEach((e) => document.addEventListener(e, kick, { passive: true }));
    kick();
    return () => { v.removeEventListener("ended", onEnded); evts.forEach((e) => document.removeEventListener(e, kick)); };
  }, [reduce]);

  const toggle = () => {
    const v = vref.current;
    if (!v) return;
    v.muted = !muted;
    setMuted(!muted);
    v.play?.()?.catch?.(() => {});
  };
  const tiltOn = !touch && !reduce;

  return (
    <div className="relative" data-testid="hero-video-wrap">
      <motion.div
        className={`group relative w-full ${RADIUS}`}
        style={{ ...(tiltOn ? tilt.style : {}), boxShadow: "0 60px 120px -50px rgba(0,0,0,0.95), 0 30px 80px -40px rgba(0,0,0,0.7), 0 0 120px -40px rgba(212,175,55,0.22)" }}
        {...(tiltOn ? tilt.handlers : {})}
        data-testid="hero-video-frame"
      >
        {/* real 16:9 height without relying on CSS aspect-ratio */}
        <div aria-hidden style={{ paddingTop: "56.25%" }} />
        <div className={`absolute inset-0 overflow-hidden ${RADIUS}`}>
          <video
            ref={vref}
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            poster={POSTER}
            autoPlay={!reduce} muted loop playsInline preload={reduce ? "metadata" : "auto"}
          >
            <source src={src} type="video/mp4" />
            <source src={WEBM} type="video/webm" />
          </video>
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 50% 45%, transparent 62%, rgba(8,7,5,0.38) 100%)" }} />
          {tiltOn && <Glare glare={tilt.glare} />}
        </div>
        <SoundToggle muted={muted} onClick={toggle} />
      </motion.div>
    </div>
  );
};

export default HeroVideo;
